import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ClockIcon,
  ExclamationTriangleIcon,
  CurrencyDollarIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/outline";
import { ProjectTimeList } from "../project-time-list";
import { useSettings } from "../../../../contexts/settings-context";

export const TimeRegistrationTab = ({
  project,
  canManageProjectTime = false,
  showHourlyCosts = false,
  restrictHoursToCurrentUserId = null,
  companyScope = false,
}) => {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [showCostCalculation, setShowCostCalculation] = useState(false);
  const [timeEntries, setTimeEntries] = useState([]);
  const [hoursLoadState, setHoursLoadState] = useState("loading");
  const handleEntriesChange = useCallback((entries) => setTimeEntries(entries), []);

  // Calculate hourly costs from time entries (only when cost UI is enabled)
  const calculateHourlyCosts = () => {
    if (!timeEntries || timeEntries.length === 0) return {};

    const userCosts = {};
    // Match the list metrics / PDF selection: cost the currently visible
    // entries (chip filters), not only approved rows from the unfiltered load.
    const included = timeEntries || [];

    included.forEach((entry) => {
      const userId = entry.user?.id;
      const userName = entry.user?.displayTitle || t("project.time_overview.ui.unknown_user", "Ukjent bruker");
      const hours = parseFloat(entry.time) || 0;
      const hourlyRate = parseFloat(entry.effective_hourly_rate) || 0;
      const hourCostRate = parseFloat(entry.effective_hour_cost_rate) || 0;
      
      if (!userCosts[userId]) {
        userCosts[userId] = {
          user: { id: userId, name: userName },
          totalHours: 0,
          totalHourlyCost: 0,
          totalHourCostRate: 0,
          hasValidRate: hourCostRate > 0,
          entries: []
        };
      }
      
      userCosts[userId].totalHours += hours;
      userCosts[userId].totalHourlyCost += hours * hourlyRate;
      userCosts[userId].totalHourCostRate += hours * hourCostRate;
      userCosts[userId].hasValidRate = userCosts[userId].hasValidRate && hourCostRate > 0;
      userCosts[userId].entries.push(entry);
    });

    return userCosts;
  };

  const hourlyCosts = showHourlyCosts ? calculateHourlyCosts() : {};
  const totalProjectSales = showHourlyCosts
    ? Object.values(hourlyCosts).reduce((sum, user) => sum + user.totalHourlyCost, 0)
    : 0;
  const totalProjectCost = showHourlyCosts
    ? Object.values(hourlyCosts).reduce((sum, user) => sum + user.totalHourCostRate, 0)
    : 0;
  const hasUsersWithoutRates = showHourlyCosts
    ? Object.values(hourlyCosts).some((user) => !user.hasValidRate)
    : false;
  const costsReady = hoursLoadState === "ready";

  return (
    <div className={`pto-shell space-y-6${settings?.theme === "dark" ? " pto--dark" : ""}`}>
      {/* Hourly Cost Calculation */}
      {showHourlyCosts ? (
      <div className="pto-cost bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            {t("project.hourly_costs")}
          </h3>
          <button
            onClick={() => setShowCostCalculation(!showCostCalculation)}
            className="flex items-center px-3 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <CurrencyDollarIcon className="h-4 w-4 mr-2" />
            {showCostCalculation
              ? t("project.time_overview.ui.hide_calculation", "Skjul beregning")
              : t("project.time_overview.ui.show_calculation", "Vis beregning")}
          </button>
        </div>

        {/* Cost Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 text-blue-500 mr-2" />
              <span className="text-sm font-medium text-blue-900">
                {t("project.time_overview.ui.total_hours", "Totalt timer")}
              </span>
            </div>
            <p className="text-2xl font-bold text-blue-900 mt-1">
              {costsReady ? `${Object.values(hourlyCosts).reduce((sum, user) => sum + user.totalHours, 0).toFixed(1)}h` : "—"}
            </p>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 text-green-500 mr-2" />
              <span className="text-sm font-medium text-green-900">
                {t("project.time_overview.ui.estimated_cost", "Estimert kostnad")}
              </span>
            </div>
            <p className="text-2xl font-bold text-green-900 mt-1">
              {costsReady ? `${totalProjectCost.toLocaleString('no-NO')} NOK` : "—"}
            </p>
          </div>

          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 text-indigo-500 mr-2" />
              <span className="text-sm font-medium text-indigo-900">
                {t("project.time_overview.ui.estimated_sales", "Estimert salg")}
              </span>
            </div>
            <p className="text-2xl font-bold text-indigo-900 mt-1">
              {costsReady ? `${totalProjectSales.toLocaleString('no-NO')} NOK` : "—"}
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <ExclamationCircleIcon className="h-5 w-5 text-yellow-500 mr-2" />
              <span className="text-sm font-medium text-yellow-900">
                {t("project.time_overview.ui.missing_hour_cost_count", "Mangler timekostnad")}
              </span>
            </div>
            <p className="text-2xl font-bold text-yellow-900 mt-1">
              {costsReady ? Object.values(hourlyCosts).filter(user => !user.hasValidRate).length : "—"}
            </p>
          </div>
        </div>

        {/* Warning for users without rates */}
        {hoursLoadState === "error" ? <p role="alert" className="mb-4 text-sm text-red-700">
          {t("project.time_overview.ui.costs_load_error", "Timekostnadene kunne ikke beregnes. Prøv igjen i timeoversikten.")}
        </p> : null}
        {hasUsersWithoutRates && costsReady && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  {t("project.hourly_costs.warning_title")}
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>{t("project.hourly_costs.missing_rate_warning")}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Detailed Cost Calculation */}
        {showCostCalculation && costsReady && (
          <div className="mt-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">
              {t("project.time_overview.ui.detailed_per_employee", "Detaljert beregning per ansatt")}
            </h4>
            <div className="space-y-3">
              {Object.values(hourlyCosts).map((userCost) => (
                <div key={userCost.user.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="font-medium text-gray-900">
                        {userCost.user.name}
                      </span>
                      {!userCost.hasValidRate && (
                        <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          {t("project.hourly_costs.missing_rate_warning")}
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {userCost.totalHours.toFixed(1)}h × {userCost.entries[0]?.effective_hour_cost_rate || 0} NOK
                      </p>
                      <p className="font-medium text-gray-900">
                        {userCost.totalHourCostRate.toLocaleString('no-NO')} NOK
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      ) : null}

      {/* Time Registration Overview */}
      <div>
        <ProjectTimeList
          project={project}
          companyScope={companyScope}
          theme={settings?.theme}
          canManageProjectTime={canManageProjectTime}
          includeHourlyCosts={showHourlyCosts}
          restrictHoursToCurrentUserId={restrictHoursToCurrentUserId}
          onEntriesChange={handleEntriesChange}
          onLoadStateChange={setHoursLoadState}
        />
      </div>
    </div>
  );
};
